import React, { useEffect, useState } from "react";
import { Pencil, Users } from "lucide-react";

import Modal from "./Modal";
import EntitySelector from "@/components/forms/EntitySelector";
import RoleDayGrid, { type RoleDayGridRow } from "@/components/sections/RoleDayGrid";
import { createRoleHeaderDayUIItem } from "@/utils/roleDayGridUtils";
import type { Employee, RoleUsersGrid } from "@/types.ts";

const DAYS = ["0", "1", "2", "3", "4", "5", "6"];
const ALL_USERS = ["*"];

const headerDays = DAYS.map((d) => createRoleHeaderDayUIItem(d));

export type RoleUsersGridModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (grid: RoleUsersGrid) => void;
    roles: { id: string; name: string }[];
    users: (string | Employee)[];
    value: RoleUsersGrid;
};

const RoleUsersGridModal: React.FC<RoleUsersGridModalProps> = ({ open, onClose, onConfirm, roles, users, value }) => {
    const [grid, setGrid] = useState<RoleUsersGrid>({});
    const [editingRoleId, setEditingRoleId] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setGrid(value ?? {});
            setEditingRoleId(null);
        }
    }, [open]);

    // Rows keep their users while all their days are unchecked, so re-checking a day restores the selection;
    // day-less rows are dropped on confirm.
    const setRowDays = (roleId: string, days: string[]) => {
        setGrid((prev) => ({
            ...prev,
            [roleId]: { days, users: prev[roleId]?.users ?? ALL_USERS },
        }));
    };

    const toggleDay = (roleId: string, dayId: string) => {
        const days = grid[roleId]?.days ?? [];
        setRowDays(roleId, days.includes(dayId) ? days.filter((d) => d !== dayId) : [...days, dayId].sort());
    };

    const toggleAllDays = (roleId: string) => {
        const days = grid[roleId]?.days ?? [];
        setRowDays(roleId, days.length === DAYS.length ? [] : [...DAYS]);
    };

    const setRowUsers = (roleId: string, selectedUsers: string[]) => {
        setGrid((prev) => ({
            ...prev,
            // An empty selection would leave the row without anyone to balance, so it falls back to everyone.
            [roleId]: { days: prev[roleId]?.days ?? [], users: selectedUsers.length > 0 ? selectedUsers : ALL_USERS },
        }));
    };

    const editingRole = roles.find((r) => r.id === editingRoleId);
    const holdersOf = (roleId: string | null) => users.filter(
        (u): u is Employee => typeof u !== "string" && (u.roles ?? []).some((r) => r.id === roleId),
    );
    const editingRoleHolders = holdersOf(editingRoleId);

    // Saved users who left the role are ignored by the solver, so they are not counted either.
    const countUsers = (roleId: string, userIds: string[]) => {
        const holders = holdersOf(roleId);
        return holders.length === 0
            ? userIds.length
            : userIds.filter((id) => holders.some((u) => (u.id ?? u.userId) === id)).length;
    };

    // Saved rows of a role absent from the schedule stay removable, unless no role is loaded at all.
    const rows = roles.length === 0 ? [] : [
        ...roles,
        ...Object.keys(grid)
            .filter((roleId) => !roles.some((r) => r.id === roleId))
            .map((roleId) => ({ id: roleId, name: `Rôle introuvable (${roleId})` })),
    ];

    const handleConfirm = () => {
        onConfirm(Object.fromEntries(Object.entries(grid).filter(([, row]) => row.days.length > 0)));
    };

    const listOfUINeeds: RoleDayGridRow[] = rows.map((role) => {
        const selectedDays = grid[role.id]?.days ?? [];
        return {
            id: role.id,
            name: role.name,
            days: DAYS.map((dayId) => ({ ...createRoleHeaderDayUIItem(dayId), value: selectedDays.includes(dayId) })),
            isAll: selectedDays.length === DAYS.length,
        };
    });

    const renderUsersCell = (roleId: string) => {
        const row = grid[roleId];
        if (!row || row.days.length === 0) {
            return null;
        }
        const role = roles.find((r) => r.id === roleId);

        return (
            <span className="flex items-center justify-center gap-2 font-semibold">
                {row.users.includes("*") ? "Tous" : (
                    <span className="inline-flex items-center gap-1">
                        {countUsers(roleId, row.users)}
                        <Users size={16} aria-hidden/>
                    </span>
                )}
                {role && (
                    <button
                        type="button"
                        aria-label={`Modifier les utilisateurs de ${role.name}`}
                        onClick={() => setEditingRoleId(roleId)}
                        className="rounded p-1 text-amber-600 cursor-pointer hover:bg-gray-100"
                    >
                        <Pencil size={16}/>
                    </button>
                )}
            </span>
        );
    };

    return (
        <>
            <Modal
                open={open}
                // Escape also reaches this modal while the users selector is open; keep the grid then.
                onClose={() => {
                    if (!editingRoleId) {
                        onClose();
                    }
                }}
                title="Grille de sélection"
                size="xl"
                footer={
                    <>
                        <button
                            onClick={onClose}
                            className="inline-flex items-center rounded-full bg-gray-200 cursor-pointer px-4 py-2 font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                        >
                            Annuler
                        </button>
                        <button
                            onClick={handleConfirm}
                            className="inline-flex items-center rounded-full bg-green-600 px-4 py-2 font-medium text-white cursor-pointer hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                            Confirmer
                        </button>
                    </>
                }
            >
                {rows.length === 0 ? (
                    <div className="py-10 text-center text-gray-500">Aucun rôle à sélectionner.</div>
                ) : (
                    <RoleDayGrid
                        headerDays={headerDays}
                        listOfUINeeds={listOfUINeeds}
                        onRoleDayClick={toggleDay}
                        onRoleAllDaysClick={toggleAllDays}
                        extraColumn={{ header: "Utilisateurs", renderCell: renderUsersCell }}
                    />
                )}
            </Modal>

            <Modal
                open={editingRoleId !== null}
                onClose={() => setEditingRoleId(null)}
                title={`Utilisateurs — ${editingRole?.name ?? ""}`}
                size="md"
                footer={
                    <button
                        onClick={() => setEditingRoleId(null)}
                        className="inline-flex items-center rounded-full bg-green-600 px-4 py-2 font-medium text-white cursor-pointer hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                        Fermer
                    </button>
                }
            >
                {/* Leaves room for the options menu, which the modal body would otherwise clip. */}
                <div className="min-h-80">
                    {editingRoleId && (
                        <EntitySelector
                            data={editingRoleHolders}
                            onSelect={(selected) => setRowUsers(editingRoleId, selected)}
                            index={0}
                            value={grid[editingRoleId]?.users ?? ALL_USERS}
                            label="Utilisateurs"
                            allLabel="Tous les utilisateurs"
                            valueKey="id"
                        />
                    )}
                </div>
            </Modal>
        </>
    );
};

export default RoleUsersGridModal;
